import { useDynamicConfig } from '@statsig/react-bindings';
import { useAtomValue } from 'jotai';
import { useParams } from 'react-router-dom';

import { useAnnouncementToast } from '../components/announcements/useAnnouncementToast';
import { useDetectStaleVersionInterval } from '../components/stale-version/useDetectStaleVersionInterval';
import { Error404Page } from '../error/404Page';
import { useIsValidProviderAndPatient } from '../hooks/useIsValidProviderAndPatient';
import { useVideoPlatform } from '../hooks/useVideoPlatform';
import { blockedTelehealthPatientShortIds } from '../statsig/gates';
import { UserType } from '../types';
import { useGetClientSafetyGuard } from '../useGetClientSafetyGuard';
import { BrowserLanguagePromptWrapper } from './BrowserLanguagePromptWrapper';
import { PageWrapper as IntroRoomPage } from './introduction-room/PageWrapper';
import { useGetClientUserPaperwork } from './measures/useGetClientUserPaperwork';
import { Page as MeetingRoomPage } from './meeting-room';
import { useRequestSystemNotificationPermission } from './notifications/useRequestSystemNotificationsPermission';
import { PageWrapper as PreSessionMeasuresPage } from './pre-session-measures/PageWrapper';
import { PageWrapper as RejoinPage } from './rejoin-page/PageWrapper';
import { VisitorState, shouldShowStaleVersionToastAtom, visitorStateAtom, visitorStateToAppState } from './state';
import { useBranchSmartBanner } from './useBranchSmartBanner';
import { useInitVisitorGlobalDDLoggerContext } from './useInitVisitorGlobalDDLoggerContext';
import { useInitVisitorUserTracking } from './useInitVisitorUserTracking';
import { useVisitorJourneySteps } from './useVisitorJourneySteps';
import { Page as WaitingRoomPage } from './waiting-room';

function MeasuresPageEffect() {
  useGetClientUserPaperwork();
  useGetClientSafetyGuard();
  return null;
}

function PageEffects({ visitorState }: { visitorState: VisitorState }) {
  const { patientShortId } = useParams<{
    patientShortId: string;
  }>();
  useInitVisitorGlobalDDLoggerContext();
  useInitVisitorUserTracking();
  useDetectStaleVersionInterval(useAtomValue(shouldShowStaleVersionToastAtom));
  const videoPlatform = useVideoPlatform();
  useAnnouncementToast({
    userType: UserType.CLIENT,
    appState: visitorStateToAppState(visitorState),
    userId: patientShortId,
    videoPlatform,
  });
  useBranchSmartBanner(patientShortId, visitorState);
  useRequestSystemNotificationPermission();

  return <MeasuresPageEffect />;
}

function PageContent({ visitorState }: { visitorState: VisitorState }) {
  switch (visitorState) {
    case VisitorState.WAITING:
    case VisitorState.JOINING:
    case VisitorState.LOADING:
      return <WaitingRoomPage />;
    case VisitorState.MEETING:
      return <MeetingRoomPage />;
    case VisitorState.PRESESSION_MEASURES:
      return <PreSessionMeasuresPage />;
    case VisitorState.TIMEOUT:
    case VisitorState.TAB_AWAY:
      return <RejoinPage />;
    default:
      return <IntroRoomPage />;
  }
}

export function Page() {
  const visitorState = useAtomValue(visitorStateAtom);
  const { providerShortId, patientShortId } = useParams<{
    providerShortId: string;
    patientShortId: string;
  }>();

  const { data, loading } = useIsValidProviderAndPatient(providerShortId!, patientShortId!);

  const isInvalidUrl = !loading && !data?.isValidProviderAndPatient;

  const blockedPatientShortIdSet = new Set(
    useDynamicConfig(blockedTelehealthPatientShortIds)?.get<string[]>('patientShortIds') ?? [],
  );

  const shouldBlockEntry = blockedPatientShortIdSet.has(patientShortId) || isInvalidUrl;

  useVisitorJourneySteps({
    isLoading: loading,
    isValidUrl: !!data?.isValidProviderAndPatient,
    isBlocked: blockedPatientShortIdSet.has(patientShortId),
  });

  /**
   * This does not fully prevent access to a Provider's waiting room,
   * since having the providerShortId and patientShortId alone is enough to join.
   *
   * However, we only distribute session links containing both the
   * providerShortId and patientShortId. This check blocks any patientShortId
   * we've explicitly chosen to deny, which should prevent most unwanted access.
   */

  if (shouldBlockEntry) return <Error404Page />;

  return (
    <>
      <BrowserLanguagePromptWrapper />
      <PageEffects visitorState={visitorState} />
      <PageContent visitorState={visitorState} />
    </>
  );
}
